import React, { useState, useEffect, useCallback } from 'react';
import styles from './Select.module.scss';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Select = ({
  isSearchable = true,
  asyncRequest,
  placeholder,
  transformData,
  staticOptions = [],
  debounceDelay = 300,
  onSelect,
}) => {
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);

  const handleSearch = useCallback(async () => {
    if (!isSearchable || debouncedSearchTerm === '') return;

    setLoading(true);
    try {
      const requestData = await asyncRequest(debouncedSearchTerm);
      const formattedOptions = transformData(requestData.data);
      setOptions(formattedOptions);
    } catch (error) {
      console.error('Error fetching options:', error);
    } finally {
      setLoading(false);
    }
  }, [asyncRequest, debouncedSearchTerm, transformData, isSearchable]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = option => {
    setSelectedOption(option);
    setIsOpen(false);
    if (onSelect) {
      onSelect(option);
    }
  };

  const handleInputChange = e => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (debouncedSearchTerm) {
      handleSearch();
    }
  }, [debouncedSearchTerm, isSearchable, handleSearch]);

  useEffect(() => {
    if (!isSearchable) {
      setOptions(staticOptions);
    }
  }, [isSearchable, staticOptions]);

  return (
    <div className={styles.selectContainer}>
      <div aria-hidden className={styles.selectBox} onClick={toggleDropdown}>
        <span>
          {selectedOption ? selectedOption.label : placeholder || '...'}
        </span>
        <span>&#9662;</span>
      </div>
      {isOpen && (
        <div className={styles.dropdown}>
          {isSearchable && (
            <input
              className={styles.searchInput}
              type="text"
              value={searchTerm}
              placeholder="Загрузка..."
              onChange={handleInputChange}
            />
          )}
          {loading ? (
            <div className={styles.option}>Загрузка...</div>
          ) : (
            options.map(option => (
              <div
                aria-hidden
                key={option.value}
                className={styles.option}
                onClick={() => handleOptionClick(option)}>
                {option.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
